<template>
  <div>
    <load-profile />
    <n-search-container
      :fields="searchFields"
      @search="search"
      @reset="reset"
    >
      <n-search-input
        ref="search"
        v-model="params"
        :fields="searchFields"
      />
    </n-search-container>

    <b-card
      no-body
      class="mb-0"
    >
      <div class="p-2">
        <b-row>
          <b-col>
            <label>{{ $t("general.entries") }}</label>
            <v-select
              v-model="perPage"
              :dir="$store.state.appConfig.isRTL ? 'rtl' : 'ltr'"
              :options="perPageOptions"
              :clearable="false"
              class="per-page-selector d-inline-block ml-50 mr-1"
            >
              <template #no-options>
                {{ $t("selection.noOption") }}
              </template>
            </v-select>
          </b-col>
          <b-col
            v-if="$can('create', permissionType)"
            class="text-right"
          >
            <b-button
              variant="primary"
              :to="{ name: `create-${route}` }"
            >{{
              $t("button.create")
            }}</b-button>
          </b-col>
        </b-row>
      </div>
      <div>
        <n-table
          ref="table"
          :fields="fields"
          :items="items"
          :busy="loading"
          :current-page="params.page"
          :per-page="$store.state.pagination.perPage"
          :total="total"
          :deletable="true"
          :type="permissionType"
          :route="route"
          @sort-changed="sortChanged"
          @destroy="destroy"
        >
          <template #cell(name)="data">
            {{
              getRoleName(data.item)
            }}
          </template>
          <template #cell(countMerchant)="data">
            {{
              $tc("general.personCount", data.item.countUser, {
                count: data.item.countUser,
              })
            }}
          </template>
          <template #cell(countUser)="data">
            <span
              v-if="data.item.countUser < 1"
              class="text-primary"
            >
              0
            </span>
            <b-link
              v-else
              :style="{ textDecoration: 'underline' }"
              @click="redirectEmployee(data.item.id)"
            >
              {{ data.item.countUser }}
            </b-link>
          </template>
          <template #cell(actions)="data">
            <div
              v-if="data.item.type != 'default-merchant'"
              class="text-nowrap"
            >
              <b-button
                v-if="$can('update', permissionType)"
                v-b-tooltip.hover
                :to="{
                  name: `update-${route}`,
                  params: { id: data.item.id },
                }"
                :title="$t('general.update')"
                variant="primary"
                pill
                size="sm"
              >
                <feather-icon icon="EditIcon" />
              </b-button>
              <b-button
                v-if="$can('delete', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.delete')"
                variant="danger"
                pill
                size="sm"
                @click="confirmDestroy(data.item.id, data.index, data.item)"
              >
                <feather-icon icon="TrashIcon" />
              </b-button>
            </div>
            <div v-else>
              <b-button
                v-if="$can('update', permissionType)"
                v-b-tooltip.hover
                :title="$t('general.defaultRoleRule')"
                variant="secondary"
                pill
                size="sm"
              >
                <feather-icon icon="LockIcon" />
              </b-button>
            </div>
          </template>
        </n-table>
        <n-pagination
          class="mt-2"
          :total="total"
          :per-page="$store.state.pagination.perPage"
          :page="query.page"
          @change="list"
        />
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BLink,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import Repository from '@/repositories/RepositoryFactory'
import NPagination from '@/components/NPagination'
import NSearchContainer from '@/components/NSearchContainer'
import NSearchInput from '@/components/NSearchInput'
import NTable from '@/components/NTable'
import { avatarText } from '@core/utils/filter'
import SearchInputs from './searchInput'
import tableFields from './tableField'

const RoleRepository = Repository.get('role')

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    NPagination,
    NSearchInput,
    NSearchContainer,
    NTable,
    vSelect,
    BLink,
  },

  data() {
    return {
      items: [],
      loading: false,
      total: 0,
      perPageOptions: this.$store.state.pagination.perPageOptions,
      params: {
        name: this.$route.query.name || null,
      },
      query: {
        page: Number(this.$route.query.page) || 1,
        order: this.$route.query.order || null,
        sort: this.$route.query.sort || null,
        name: this.$route.query.name || null,
      },
      avatarText,
    }
  },
  computed: {
    perPage: {
      get() {
        return this.$store.state.pagination.perPage
      },
      set(value) {
        this.$store.commit('pagination/SET_PER_PAGE', value)
      },
    },
    activeSearch() {
      return !!this.$route.query.name || !!this.$route.query.isEnable
    },
    locale() {
      return this.$i18n.locale.split('-')[0]
    },
  },
  watch: {
    perPage(value) {
      this.list(1)
    },
  },
  mounted() {
    this.getData(this.query.page)
  },
  methods: {
    getRoleName(data) {
      let name = data?.name
      if (!name || typeof name === 'object') {
        name = data?.name?.[this.locale] || data?.translations?.en || '-'
      }
      // Handle Super Admin translation
      if (name === 'Super Admin') {
        return this.$t('field.superAdmin')
      }
      return name
    },
    updateQuerySting() {
      this.$router.replace({ query: this.query })
    },
    list(page = 1) {
      this.query.page = page
      this.updateQuerySting()
      this.getData()
    },
    sortChanged(sortBy, sortDirection) {
      this.query.order = sortBy
      this.query.sort = sortDirection
      this.updateQuerySting()
      this.getData()
    },
    search() {
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    reset() {
      this.$refs.search.reset()
      this.query = {
        ...this.query,
        ...this.params,
      }
      this.updateQuerySting()
      this.getData()
    },
    getData() {
      this.loading = true
      RoleRepository.index({
        ...this.query,
      })
        .then(response => {
          const data = response.data.data.list
          this.items = data
          this.total = response.data.data.total
        })
        .catch(() => {})
        .then(() => {
          this.loading = false
        })
    },
    confirmDestroy(id, index, data) {
      index = this.$refs.table.calculateOrderNumber(index)

      this.$bvModal
        .msgBoxConfirm(
          this.$t('alert.unableToRevertAfterDelete', {
            item: `#${index} ${data.name?.[this.locale] || data?.name?.en || data?.name?.zh || data?.name?.km || data?.name?.vi || data.name || '-'}`,
          }),
          {
            title: this.$t('alert.areYouSure'),
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: this.$t('button.yesDelete'),
            cancelTitle: this.$t('button.cancel'),
            cancelVariant: 'outline-secondary',
            hideHeaderClose: false,
            centered: true,
          },
        )
        .then(value => {
          if (value) {
            this.destroy(id)
          }
        })
    },
    destroy(id) {
      RoleRepository.destroy(id)
        .then(response => {
          this.getData()
        })
        .catch(error => {})
        .then(() => {
          this.loading = false
        })
    },
    redirectEmployee(id) {
      const route = this.$router.resolve({
        name: 'list-operator',
        query: { roleId: id, from: 'role' },
      })
      window.open(route.href, '_blank')
    },
  },
  setup() {
    const fields = tableFields
    const searchFields = SearchInputs
    const permissionType = 'manage-role'
    const route = 'role'

    return {
      fields,
      searchFields,
      permissionType,
      route,
    }
  },
}
</script>

<style lang="scss">
@import "@core/scss/vue/libs/vue-select.scss";
</style>
